import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { 
  CreditCard, Copy, Users, Clock, DollarSign, 
  Sun, Moon, Loader2, CheckCircle, AlertTriangle, ExternalLink,
  Pencil, Crown, UserPlus, Trash2, Save, Gift, XCircle, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow, parseISO } from 'date-fns';

export default function SettingsPage() {
  const { api, member, isChair } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [forumDetails, setForumDetails] = useState(null);
  const [members, setMembers] = useState([]);
  const [inviteCode, setInviteCode] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  
  // Forum rename
  const [editingName, setEditingName] = useState(false);
  const [newForumName, setNewForumName] = useState('');
  const [savingName, setSavingName] = useState(false);
  
  // Invite member
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: '', email: '' });
  const [inviting, setInviting] = useState(false);
  
  // Change moderator
  const [moderatorDialogOpen, setModeratorDialogOpen] = useState(false);
  const [selectedModerator, setSelectedModerator] = useState('');
  const [changingModerator, setChangingModerator] = useState(false);
  
  // Cancel subscription
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelImmediately, setCancelImmediately] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [subRes, detailsRes, membersRes] = await Promise.all([
        api().get('/subscription/status'),
        api().get('/forum/details'),
        api().get('/members'),
      ]);
      setSubscription(subRes.data);
      setForumDetails(detailsRes.data);
      setInviteCode(detailsRes.data.invite_code || '');
      setNewForumName(detailsRes.data.name || '');
      setMembers(membersRes.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCopyInviteCode = () => {
    navigator.clipboard.writeText(inviteCode);
    toast.success('Invite code copied to clipboard!');
  };

  const handleCopyInviteLink = () => {
    const link = `${window.location.origin}/register?invite=${inviteCode}`;
    navigator.clipboard.writeText(link);
    toast.success('Invite link copied to clipboard!');
  };

  const handleSubscribe = async () => {
    setCheckoutLoading(true);
    try {
      const response = await api().post('/payments/create-checkout', {}, {
        headers: { 'Origin': window.location.origin }
      });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Checkout failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setCheckoutLoading(false);
    }
  };

  const handleSaveForumName = async () => {
    if (!newForumName.trim()) {
      toast.error('Forum name cannot be empty');
      return;
    }
    
    setSavingName(true);
    try {
      await api().put('/forum/update', { name: newForumName.trim() });
      setForumDetails({ ...forumDetails, name: newForumName.trim() });
      setEditingName(false);
      toast.success('Forum name updated!');
      // Trigger layout refresh
      window.dispatchEvent(new Event('forumUpdated'));
    } catch (error) {
      toast.error('Failed to update forum name');
    } finally {
      setSavingName(false);
    }
  };

  const handleInviteMember = async () => {
    if (!inviteForm.name.trim() || !inviteForm.email.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setInviting(true);
    try {
      const response = await api().post('/members/invite', {
        name: inviteForm.name.trim(),
        email: inviteForm.email.trim().toLowerCase(),
        forum_id: forumDetails.id,
        home_timezone: 'UTC',
      });
      setMembers([...members, response.data]);
      setInviteDialogOpen(false);
      setInviteForm({ name: '', email: '' });
      toast.success(`Invitation sent to ${inviteForm.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite member');
    } finally {
      setInviting(false);
    }
  };

  const handleDeleteMember = async (memberId, memberName) => {
    try {
      await api().delete(`/members/${memberId}`);
      setMembers(members.filter(m => m.id !== memberId));
      toast.success(`${memberName} has been removed`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleChangeModerator = async () => {
    if (!selectedModerator) {
      toast.error('Please select a member');
      return;
    }
    
    setChangingModerator(true);
    try {
      await api().put('/forum/moderator', { member_id: selectedModerator });
      await fetchData(); // Refresh all data
      setModeratorDialogOpen(false);
      toast.success('Forum moderator updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change moderator');
    } finally {
      setChangingModerator(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const response = await api().post('/subscription/cancel', {
        cancel_immediately: cancelImmediately,
        reason: cancelReason || null
      });
      setCancelDialogOpen(false);
      setCancelReason('');
      setCancelImmediately(false);
      toast.success(response.data.message);
      await fetchData(); // Refresh subscription status
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setReactivating(true);
    try {
      const response = await api().post('/subscription/reactivate');
      toast.success(response.data.message);
      await fetchData(); // Refresh subscription status
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate subscription');
    } finally {
      setReactivating(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500 gap-1"><CheckCircle className="h-3 w-3" /> Active</Badge>;
      case 'trial':
        return <Badge variant="secondary" className="gap-1"><Clock className="h-3 w-3" /> Trial</Badge>;
      case 'free':
        return <Badge className="bg-purple-500 gap-1"><Gift className="h-3 w-3" /> Free</Badge>;
      case 'expired':
        return <Badge variant="destructive" className="gap-1"><AlertTriangle className="h-3 w-3" /> Expired</Badge>;
      case 'cancelled':
        return <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" /> Cancelled</Badge>;
      case 'cancelling':
        return <Badge variant="outline" className="gap-1 text-orange-600 border-orange-600"><Clock className="h-3 w-3" /> Cancelling</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground mt-1">Manage your forum and subscription</p>
      </div>

      {/* Forum Details Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5" />
            Forum Details
          </CardTitle>
          <CardDescription>
            Manage your forum name and settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Forum Name</Label>
            {editingName ? (
              <div className="flex gap-2">
                <Input
                  value={newForumName}
                  onChange={(e) => setNewForumName(e.target.value)}
                  placeholder="Enter forum name"
                  data-testid="forum-name-input"
                />
                <Button onClick={handleSaveForumName} disabled={savingName} data-testid="save-forum-name-btn">
                  {savingName ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                </Button>
                <Button variant="outline" onClick={() => { setEditingName(false); setNewForumName(forumDetails?.name || ''); }}>
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
                <span className="font-medium">{forumDetails?.name || 'Unnamed Forum'}</span>
                <Button variant="ghost" size="sm" onClick={() => setEditingName(true)} data-testid="edit-forum-name-btn">
                  <Pencil className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Moderator Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5" />
            Forum Moderator
          </CardTitle>
          <CardDescription>
            The moderator can manage meetings and has additional permissions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
            <div className="flex items-center gap-3">
              <Crown className="h-5 w-5 text-yellow-500" />
              <div>
                <p className="font-medium">{forumDetails?.chair?.name || 'Not set'}</p>
                <p className="text-sm text-muted-foreground">{forumDetails?.chair?.email}</p>
              </div>
            </div>
            
            {isChair && (
              <Dialog open={moderatorDialogOpen} onOpenChange={setModeratorDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="change-moderator-btn">
                    Change
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Change Forum Moderator</DialogTitle>
                    <DialogDescription>
                      Select a member to become the new forum moderator. You will lose your moderator privileges.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="py-4">
                    <Label>Select New Moderator</Label>
                    <Select value={selectedModerator} onValueChange={setSelectedModerator}>
                      <SelectTrigger className="mt-2" data-testid="moderator-select">
                        <SelectValue placeholder="Select a member" />
                      </SelectTrigger>
                      <SelectContent>
                        {members.filter(m => m.id !== member.id).map((m) => (
                          <SelectItem key={m.id} value={m.id}>
                            {m.name} ({m.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setModeratorDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleChangeModerator} disabled={changingModerator || !selectedModerator} data-testid="confirm-moderator-btn">
                      {changingModerator ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Transfer Moderator Role
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Members Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Forum Members ({members.length})
              </CardTitle>
              <CardDescription>
                Manage members of your forum
              </CardDescription>
            </div>
            
            <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2" data-testid="invite-member-btn">
                  <UserPlus className="h-4 w-4" />
                  Invite
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Invite Member</DialogTitle>
                  <DialogDescription>
                    Send an invitation email to add a new member to your forum.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="invite-name">Full Name</Label>
                    <Input
                      id="invite-name"
                      placeholder="John Smith"
                      value={inviteForm.name}
                      onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                      data-testid="invite-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="invite-email">Email Address</Label>
                    <Input
                      id="invite-email"
                      type="email"
                      placeholder="john@example.com"
                      value={inviteForm.email}
                      onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                      data-testid="invite-email-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleInviteMember} disabled={inviting} data-testid="send-invite-btn">
                    {inviting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Send Invitation
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {members.map((m) => (
              <div key={m.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                <div className="flex items-center gap-3">
                  {m.id === forumDetails?.owner_id && (
                    <Crown className="h-4 w-4 text-yellow-500" />
                  )}
                  <div>
                    <p className="font-medium">{m.name}</p>
                    <p className="text-sm text-muted-foreground">{m.email}</p>
                  </div>
                </div>
                
                {m.id !== member.id && m.id !== forumDetails?.owner_id && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" data-testid={`remove-member-${m.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remove Member</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to remove {m.name} from the forum? They will no longer have access.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDeleteMember(m.id, m.name)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                          Remove
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Invite Code Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Copy className="h-5 w-5" />
            Invite Link
          </CardTitle>
          <CardDescription>
            Share this code or link with new members to join your forum
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Invite Code</Label>
            <div className="flex gap-2">
              <Input 
                value={inviteCode} 
                readOnly 
                className="font-mono bg-muted" 
                data-testid="invite-code-display"
              />
              <Button variant="outline" onClick={handleCopyInviteCode} data-testid="copy-code-btn">
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          <Button variant="secondary" onClick={handleCopyInviteLink} className="w-full gap-2" data-testid="copy-link-btn">
            <Copy className="h-4 w-4" />
            Copy Invite Link
          </Button>
        </CardContent>
      </Card>

      {/* Subscription Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Subscription
          </CardTitle>
          <CardDescription>
            Manage your forum's subscription and billing
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Status</span>
            {getStatusBadge(subscription?.status)}
          </div>

          {subscription?.status === 'free' && (
            <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/20">
              <div className="flex items-start gap-3">
                <Gift className="h-5 w-5 text-purple-600 mt-0.5" />
                <div>
                  <p className="font-medium text-purple-700 dark:text-purple-400">
                    Free Account
                  </p>
                  <p className="text-sm text-purple-600 dark:text-purple-300">
                    Your forum has been granted free access with no charges.
                  </p>
                </div>
              </div>
            </div>
          )}

          {subscription?.status === 'trial' && (
            <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-700 dark:text-blue-400">
                    Trial Period Active
                  </p>
                  <p className="text-sm text-blue-600 dark:text-blue-300">
                    Your trial ends{' '}
                    {subscription.trial_ends_at && 
                      formatDistanceToNow(parseISO(subscription.trial_ends_at), { addSuffix: true })}
                  </p>
                </div>
              </div>
            </div>
          )}

          {subscription?.status === 'expired' && (
            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                <div>
                  <p className="font-medium text-red-700 dark:text-red-400">
                    Trial Expired
                  </p>
                  <p className="text-sm text-red-600 dark:text-red-300">
                    Subscribe now to continue using Forum Planner
                  </p>
                </div>
              </div>
            </div>
          )}

          {!subscription?.is_free_account && (
            <>
              <Separator />

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Forum Members
                  </span>
                  <span className="font-semibold">{subscription?.member_count}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Price per Member
                  </span>
                  <span className="font-semibold">${subscription?.price_per_user}/month</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Minimum Term</span>
                  <span className="font-semibold">{subscription?.min_months} months</span>
                </div>
                <Separator />
                <div className="flex items-center justify-between text-lg">
                  <span className="font-medium">Annual Total</span>
                  <span className="font-bold">${subscription?.total_annual_cost?.toFixed(2)}</span>
                </div>
              </div>

              {(subscription?.status === 'trial' || subscription?.status === 'expired') && isChair && (
                <Button 
                  onClick={handleSubscribe} 
                  className="w-full gap-2" 
                  size="lg"
                  disabled={checkoutLoading}
                  data-testid="subscribe-btn"
                >
                  {checkoutLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <CreditCard className="h-4 w-4" />
                      Subscribe Now
                      <ExternalLink className="h-4 w-4" />
                    </>
                  )}
                </Button>
              )}
            </>
          )}

          {subscription?.status === 'active' && (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-medium">Subscription Active</span>
                </div>
              </div>
              
              {isChair && subscription?.can_cancel && (
                <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full text-destructive hover:text-destructive gap-2" data-testid="cancel-subscription-btn">
                      <XCircle className="h-4 w-4" />
                      Cancel Subscription
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Cancel Subscription</DialogTitle>
                      <DialogDescription>
                        Are you sure you want to cancel your subscription? You can choose to cancel immediately or at the end of your current billing period.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="flex items-center space-x-3">
                        <Switch
                          id="cancel-immediately"
                          checked={cancelImmediately}
                          onCheckedChange={setCancelImmediately}
                          data-testid="cancel-immediately-toggle"
                        />
                        <Label htmlFor="cancel-immediately" className="flex flex-col">
                          <span>Cancel Immediately</span>
                          <span className="text-sm text-muted-foreground font-normal">
                            {cancelImmediately 
                              ? "Your subscription will end right away" 
                              : "Your subscription will end at the end of the current billing period"}
                          </span>
                        </Label>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="cancel-reason">Reason for cancelling (optional)</Label>
                        <Textarea
                          id="cancel-reason"
                          placeholder="Let us know why you're cancelling..."
                          value={cancelReason}
                          onChange={(e) => setCancelReason(e.target.value)}
                          data-testid="cancel-reason-input"
                        />
                      </div>
                      
                      <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                        <div className="flex items-start gap-2 text-yellow-700 dark:text-yellow-400 text-sm">
                          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                          <span>
                            {cancelImmediately 
                              ? "Your access will end immediately. This cannot be undone."
                              : "You'll retain access until the end of your billing period. You can reactivate before then."}
                          </span>
                        </div>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>
                        Keep Subscription
                      </Button>
                      <Button 
                        variant="destructive" 
                        onClick={handleCancelSubscription} 
                        disabled={cancelling}
                        data-testid="confirm-cancel-btn"
                      >
                        {cancelling && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                        {cancelImmediately ? "Cancel Now" : "Cancel at Period End"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          )}
          
          {subscription?.status === 'cancelling' && (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-orange-500/10 border border-orange-500/20">
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-orange-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-orange-700 dark:text-orange-400">
                      Cancellation Pending
                    </p>
                    <p className="text-sm text-orange-600 dark:text-orange-300">
                      Your subscription will end at the end of the current billing period. You'll retain access until then.
                    </p>
                  </div>
                </div>
              </div>
              
              {isChair && (
                <Button 
                  onClick={handleReactivateSubscription} 
                  className="w-full gap-2"
                  disabled={reactivating}
                  data-testid="reactivate-subscription-btn"
                >
                  {reactivating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4" />
                      Reactivate Subscription
                    </>
                  )}
                </Button>
              )}
            </div>
          )}
          
          {subscription?.status === 'cancelled' && (
            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
              <div className="flex items-start gap-3">
                <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
                <div>
                  <p className="font-medium text-red-700 dark:text-red-400">
                    Subscription Cancelled
                  </p>
                  <p className="text-sm text-red-600 dark:text-red-300">
                    Your subscription has been cancelled. Subscribe again to continue using Forum Planner.
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Appearance Card */}
      <Card>
        <CardHeader>
          <CardTitle>Appearance</CardTitle>
          <CardDescription>
            Customize how the app looks
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {theme === 'dark' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
              <div>
                <p className="font-medium">Dark Mode</p>
                <p className="text-sm text-muted-foreground">
                  {theme === 'dark' ? 'Currently using dark theme' : 'Currently using light theme'}
                </p>
              </div>
            </div>
            <Switch
              checked={theme === 'dark'}
              onCheckedChange={toggleTheme}
              data-testid="theme-toggle"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
